import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  IconButton,
  Button,
  Tooltip,
  Avatar,
  Chip,
  Card,
  CardContent,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Lock as LockIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Download as DownloadIcon,
  Analytics as AnalyticsIcon,
  PictureAsPdf as PdfIcon,
  Person as PersonIcon,
  School as UniversityIcon,
  Public as InternationalIcon,
  Home as DomesticIcon,
  ModelTraining as TrainingIcon,
  CalendarToday as CalendarIcon,
  LocationOn as LocationIcon,
  Assessment as AssessmentIcon,
} from '@mui/icons-material';
import FilterBar from '../../common/FilterBar';
import Header from '../../common/Header';
import Loading from '../../common/Loading';
import StatCards from '../../common/StatCards';
import TableList from '../../common/TableList';
import ListBulkToolbar from '../../common/ListBulkToolbar';
import { ANALYSIS_BULK_CONFIG } from '../../../utils/bulkUpdateConfigs';
import { useListBulkSelection, bulkTableProps, createBulkHandlers } from '../../../utils/listBulkSelection';

import ConfirmDeleteDialog from '../../common/ConfirmDeleteDialog';
import FeedbackMessage from '../../common/FeedbackMessage';
import axiosInstance from '../../../utils/axios';
import { useAuth } from '../../../contexts/AuthContext';
import { canManageAnalyses } from '../../../utils/analysisPermissions';
import { getAnalysisDocumentFilename } from '../../../utils/analysisDocument';
import { downloadAnalysisDocument } from '../../../utils/protectedFilePreview';
import { format } from 'date-fns';
import { motion } from 'framer-motion';
import { SUCCESS, glassCardSx } from '../../common/SaaSTokens';

// ─── Design tokens — cyberpunk dark system ────────────────────────────────────
const B = {
  600: '#2563EB',
  700: '#1D4ED8',
  900: '#1E3A8A',
};

const NEON  = '#38BDF8';
const NEON2 = '#818CF8';
const NEON3 = '#34D399';

const SURFACE_BASE = '#050d1a';
const SURFACE_MID  = '#0a1628';

const DOT_GRID   = `url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle cx='10' cy='10' r='0.55' fill='%2338BDF8' fill-opacity='0.07'/%3E%3C/svg%3E")`;

const AnalysisList = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { user } = useAuth();
  const { selectedIds, setSelectedIds, clearSelection } = useListBulkSelection();

  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    type: 'all',
    country: 'all',
    publicity: 'all',
  });
  const [activeStatFilter, setActiveStatFilter] = useState('all');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);
  const MotionCard = motion(Card);

  // ─── Card shell ───────────────────────────────────────────────────────────────
  const cardShellSx = glassCardSx;

  const canManage = canManageAnalyses(user);

  const filterOptions = [
    {
      name: 'type',
      label: 'Type',
      type: 'select',
      options: [
        { value: 'all', label: 'All Types' },
        { value: 'training', label: 'Training' },
        { value: 'domestic', label: 'Domestic' },
        { value: 'international', label: 'International' }
      ]
    },
    ...(canManage
      ? [{
          name: 'publicity',
          label: 'Visibility',
          type: 'select',
          options: [
            { value: 'all', label: 'All' },
            { value: 'public', label: 'Public' },
            { value: 'private', label: 'Private' },
          ],
        }]
      : []),
    {
      name: 'country',
      label: 'Country',
      type: 'select',
      options: [
        { value: 'all', label: 'All Countries' },
        { value: 'malaysia', label: 'Malaysia' },
        { value: 'thailand', label: 'Thailand' },
        { value: 'indonesia', label: 'Indonesia' },
        { value: 'vietnam', label: 'Vietnam' },
        { value: 'cambodia', label: 'Cambodia' },
        { value: 'laos', label: 'Laos' },
        { value: 'china', label: 'China' },
        { value: 'hong_kong', label: 'Hong Kong' },
        { value: 'egypt', label: 'Egypt' },
        { value: 'fiji', label: 'Fiji' },
        { value: 'japan', label: 'Japan' },
        { value: 'india', label: 'India' },
        { value: 'sri_lanka', label: 'Sri Lanka' },
        { value: 'nepal', label: 'Nepal' },
        { value: 'mongolia', label: 'Mongolia' },
      ]
    }
  ];

  useEffect(() => {
    fetchAnalyses();
  }, []);

  const fetchAnalyses = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/analyses');
      setAnalyses(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching analyses:', err);
      setError('Failed to load analyses. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (analysis) => {
    if (!canManage) {
      setError('You do not have permission to delete analyses');
      return;
    }
    setSelectedAnalysis(analysis);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      const analysisName = selectedAnalysis.name;
      await axiosInstance.delete(`/analyses/${selectedAnalysis.id}`);
      setAnalyses(analyses.filter(a => a.id !== selectedAnalysis.id));
      setDeleteDialogOpen(false);
      setSelectedAnalysis(null);
      setSuccess(`Analysis "${analysisName}" has been deleted successfully!`);
    } catch (error) {
      console.error('Error deleting analysis:', error);
      setError('Failed to delete analysis. Please try again.');
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setPage(0);
  };

  const handleDownloadDocument = async (analysis) => {
    if (!analysis?.document_pdf) return;
    try {
      await downloadAnalysisDocument(
        analysis.id,
        getAnalysisDocumentFilename(analysis.name, analysis.document_pdf),
      );
    } catch (e) {
      console.error(e);
      setError('Failed to download document.');
    }
  };

  const handleClearFilters = () => {
    setFilters({ type: 'all', country: 'all', publicity: 'all' });
    setActiveStatFilter('all');
    setSearchTerm('');
    setPage(0);
  };

  const handleStatCardClick = (_, index) => {
    const filterMap = ['all', 'training', 'domestic', 'international'];
    const selectedFilter = filterMap[index] || 'all';
    const nextFilter = activeStatFilter === selectedFilter ? 'all' : selectedFilter;
    setActiveStatFilter(nextFilter);

    const nextFilters = { ...filters };
    if (nextFilter === 'all') {
      nextFilters.type = 'all';
      nextFilters.country = 'all';
    } else {
      nextFilters.type = nextFilter;
      nextFilters.country = 'all';
    }
    setFilters(nextFilters);
    setPage(0);
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'training':
        return 'primary';
      case 'domestic':
        return 'success';
      case 'international':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'training':
        return <TrainingIcon fontSize="small" />;
      case 'domestic':
        return <DomesticIcon fontSize="small" />;
      case 'international':
        return <InternationalIcon fontSize="small" />;
      default:
        return null;
    }
  };

  const getPublicityColor = (publicity) => (publicity === 'private' ? 'warning' : 'info');

  const filteredAnalyses = analyses.filter(analysis => {
    const matchesType = filters.type === 'all' || analysis.type === filters.type;
    const matchesPublicity =
      !canManage || filters.publicity === 'all' || analysis.publicity === filters.publicity;
    const matchesCountry = filters.country === 'all' || analysis.country.toLowerCase().replace(' ', '_') === filters.country;
    const matchesSearch = 
      analysis.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      analysis.university.toLowerCase().includes(searchTerm.toLowerCase()) ||
      analysis.country.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (analysis.user?.name && analysis.user.name.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesType && matchesPublicity && matchesCountry && matchesSearch;
  });

  // ─── STATS CALCULATED FROM ALL DATA (NOT FILTERED) ───────────────────────────────
  const statsData = [
    {
      icon: AssessmentIcon,
      value: analyses.length,
      label: 'Total Analyses',
      color: 'info'
    },
    {
      icon: TrainingIcon,
      value: analyses.filter(a => a.type === 'training').length,
      label: 'Training',
      color: 'primary'
    },
    {
      icon: DomesticIcon,
      value: analyses.filter(a => a.type === 'domestic').length,
      label: 'Domestic',
      color: 'success'
    },
    {
      icon: InternationalIcon,
      value: analyses.filter(a => a.type === 'international').length,
      label: 'International',
      color: 'warning'
    }
  ];

  // Define columns for TableList
  const columns = [
    {
      key: 'name',
      label: 'Analysis Name',
      icon: AnalyticsIcon,
      minWidth: 240,
      render: (analysis) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ bgcolor: theme.palette.error.main, width: 32, height: 32 }}>
            <PdfIcon fontSize="small" />
          </Avatar>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {analysis.name}
            </Typography>
            <Typography variant="caption" sx={{ color: alpha(theme.palette.common.white, 0.72) }}>
              Research Analysis
            </Typography>
          </Box>
        </Box>
      )
    },
    {
      key: 'type',
      label: 'Type',
      icon: AssessmentIcon,
      minWidth: 140,
      render: (analysis) => (
        <Chip
          icon={getTypeIcon(analysis.type)}
          label={analysis.type.charAt(0).toUpperCase() + analysis.type.slice(1)}
          color={getTypeColor(analysis.type)}
          size="small"
        />
      )
    },
    { 
      key: 'university', 
      label: 'University', 
      icon: UniversityIcon, 
      minWidth: 160,
      render: (analysis) => (
        <Typography variant="body2" sx={{ color: alpha(theme.palette.common.white, 0.9) }}>
          {analysis.university}
        </Typography>
      )
    },
    { 
      key: 'country', 
      label: 'Country', 
      icon: LocationIcon, 
      minWidth: 140,
      render: (analysis) => (
        <Typography variant="body2" sx={{ color: alpha(theme.palette.common.white, 0.9) }}>
          {analysis.country}
        </Typography>
      )
    },
    ...(canManage
      ? [{
          key: 'publicity',
          label: 'Publicity',
          icon: VisibilityIcon,
          minWidth: 120,
          render: (analysis) => (
            <Chip
              icon={analysis.publicity === 'private' ? <LockIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
              label={analysis.publicity === 'private' ? 'Private' : 'Public'}
              color={getPublicityColor(analysis.publicity)}
              size="small"
            />
          ),
        }]
      : []),
    {
      key: 'actions',
      label: 'Actions',
      align: 'right',
      sortable: false,
      minWidth: 170,
      render: (analysis) => (
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <Tooltip title="View Details" arrow>
            <IconButton
              onClick={(e) => { e.stopPropagation(); navigate(`/analyses/${analysis.id}`); }}
              size="small"
              sx={{
                color: theme.palette.info.main,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.info.main, 0.1),
                }
              }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Download document" arrow>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                handleDownloadDocument(analysis);
              }}
              size="small"
              sx={{
                color: theme.palette.success.main,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.success.main, 0.1),
                }
              }}
            >
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {canManage && (
            <>
              <Tooltip title="Edit Analysis" arrow>
                <IconButton
                  onClick={(e) => { e.stopPropagation(); navigate(`/analyses/edit/${analysis.id}`, { state: { analysis } }); }}
                  size="small"
                  sx={{
                    color: theme.palette.warning.main,
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.warning.main, 0.1),
                    }
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete Analysis" arrow>
                <IconButton
                  onClick={(e) => { e.stopPropagation(); handleDelete(analysis); }}
                  size="small"
                  sx={{
                    color: theme.palette.error.main,
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.error.main, 0.1),
                    }
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
      )
    }
  ];

  if (loading) {
    return (  
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', gap: 2 }}>
        <Loading message="Loading analyses list" size={56} variant="primary" />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header Section */}
      <Header
        title="Analysis Management"
        subtitle={
          canManage
            ? 'Track and manage research and training analyses (public or private visibility)'
            : 'Browse analyses shared as public with all members'
        }
        icon={AnalyticsIcon}
        actionButton={canManage ? (
          <Button
            variant="contained"
            size="large"
            startIcon={<AddIcon />}
            onClick={() => navigate('/analyses/add')}
            sx={{
              bgcolor: 'rgba(255, 255, 255, 0.22)',
              backdropFilter: 'blur(10px)',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.34)',
              },
              borderRadius: 2,
              px: 2.5,
              py: 1.5,
            }}
          >
            Add Analysis
          </Button>
        ) : null}
      />

      {/* Error Message */}
      <FeedbackMessage
        message={error}
        severity="error"
        onClose={() => setError(null)}
        sx={{
          fontFamily: "'DM Sans', sans-serif", fontSize: '0.9rem', fontWeight: 500,
          background: alpha('#450a0a', 0.92),
          border: '1px solid rgba(248,113,113,0.3)',
          boxShadow: '0 4px 20px rgba(239,68,68,0.12)',
          color: '#fca5a5',
          mb: 3,
        }}
      />

      {/* Success Message */}
      <FeedbackMessage
        message={success}
        severity="success"
        onClose={() => setSuccess(null)}
        sx={{
          fontFamily: "'DM Sans', sans-serif", fontSize: '0.9rem', fontWeight: 500,
          background: alpha(SUCCESS, 0.12),
          border: `1px solid ${alpha(SUCCESS, 0.25)}`,
          color: alpha(SUCCESS, 0.95),
          mb: 2,
        }}
      />

      {/* Stats Cards */}
      <StatCards
        stats={statsData}
        onCardClick={handleStatCardClick}
        activeIndex={
          activeStatFilter === 'training' ? 1 :
          activeStatFilter === 'domestic' ? 2 :
          activeStatFilter === 'international' ? 3 :
          0
        }
      />

      {/* Enhanced Filter Bar */}
      <MotionCard
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.16 }}
        elevation={0}
        sx={{ ...cardShellSx, mb: 2.5 }}
      >
        <Box sx={{ position: 'absolute', inset: 0, background: DOT_GRID, pointerEvents: 'none', zIndex: 0 }} />
        <CardContent sx={{ p: 2.5, position: 'relative', zIndex: 1 }}>
          <FilterBar
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            showFilters={showFilters}
            onToggleFilters={() => setShowFilters(!showFilters)}
            filters={filters}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
            filterOptions={filterOptions}
            searchPlaceholder="Search by name, university, country, or user..."
          />
        </CardContent>
      </MotionCard>

      {/* Analyses Table */}
      <ListBulkToolbar
        config={ANALYSIS_BULK_CONFIG}
        currentUser={user}
        selectedIds={selectedIds}
        onClearSelection={clearSelection}
        {...createBulkHandlers({
          endpoint: ANALYSIS_BULK_CONFIG.endpoint,
          setItems: setAnalyses,
          selectedIds,
          clearSelection,
          setSuccess,
          successLabel: 'analysis(s)',
          refetch: fetchAnalyses,
          setError
        })}
      />


      <TableList
        columns={columns}
        data={filteredAnalyses}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        {...bulkTableProps(ANALYSIS_BULK_CONFIG, user, selectedIds, setSelectedIds)}
        loading={loading}
        emptyState={{
          icon: <AssessmentIcon />,
          title: 'No analyses found',
          description: searchTerm || filters.type !== 'all' || filters.country !== 'all'
            ? "Try adjusting your filters to see more analyses."
            : "Get started by creating your first analysis."
        }}
        onRowClick={(analysis) => navigate(`/analyses/${analysis.id}`)}
      />

      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setSelectedAnalysis(null);
        }}
        onConfirm={confirmDelete}
        itemType="analysis"
        itemName={selectedAnalysis?.name}
        description="This action cannot be undone and will permanently remove the analysis data."
        confirmLabel="Delete Analysis"
      />
    </Container>
  );
};

export default AnalysisList;